import {Component, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subscription} from "rxjs";
import {DjangoApiService} from "../../../shared/services/django-api.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {DatePipe} from "@angular/common";
import {LanguageService} from "../../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {Phase} from "../../../core/interfaces/phase.interface";
import {CardsBasicComponent} from "../../../shared/components/cards/cards-basic/cards-basic.component";
import {MatList, MatListItem, MatListItemTitle, MatNavList} from "@angular/material/list";
import {MatIcon, MatIconModule} from "@angular/material/icon";
import {FormsModule} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerModule,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {provideNativeDateAdapter} from "@angular/material/core";
import {ActivatedRoute} from "@angular/router";
import {MatCheckbox} from "@angular/material/checkbox";
import {DialogConfirmComponent} from "../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component";
import {MatDialog} from "@angular/material/dialog";

@Component({
  selector: 'app-phase-list',
  standalone: true,
  imports: [
    TranslateModule,
    CardsBasicComponent,
    MatList,
    MatNavList,
    MatListItem,
    MatListItemTitle,
    MatIcon,
    FormsModule,
    MatFormField,
    MatInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerInput,
    MatButton,
    MatProgressSpinner,
    MatIconButton,
    MatLabel,
    MatCheckbox,
    MatDatepickerModule,
    MatInputModule,
    MatIconModule
  ],
  providers: [provideNativeDateAdapter(), DatePipe],
  templateUrl: './phase-list.component.html',
  styleUrl: './phase-list.component.css'
})
export class PhaseListComponent implements OnInit, OnDestroy {
  phaseListData: any[] = [];
  isFormVisible: boolean = false;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  phase!: any;
  productId!: any;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    private _datePipe: DatePipe,
    public _route: ActivatedRoute,
    private _languageService: LanguageService,
    private _translate: TranslateService,
    private _dialog: MatDialog) {
  }


  ngOnInit(): void {
    this.getRouteParams()
    this.loadPhaseListData()
    this.phase = this.createEmptyPhase();
  }

  private getRouteParams(): void {
    this._route.params.subscribe(params => {
      this.productId = this._route.snapshot.parent?.params['productId'];
      this.setupLanguageSubscription();
    });
  }


  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }


  loadPhaseListData() {
    this.subscriptions.add(
      this._apiService.fetchPhaseList(this.productId).subscribe({
        next: (response: any) => {
          this.phaseListData = response;
        },
        error: (error) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }


  private createEmptyPhase(): Phase {
    const emptyTranslations = {
      en: {title: '', description: ''},
      de: {title: '', description: ''}
    };
    return new Phase(emptyTranslations, '', '');
  }


  toggleFormVisibility(): void {
    this.isFormVisible = !this.isFormVisible;
    this.resetFormData();
  }


  private resetFormData(): void {
    this.phase = this.createEmptyPhase();
    this.isEditMode = false;
  }


  private formatDate(date: Date): string {
    return this._datePipe.transform(date, 'yyyy-MM-dd') || '';
  }


  savePhase(): void {
    if (this.isEditMode) {
      this.updatePhase();
    } else {
      this.createPhase();
    }
  }


  private applyDateFormatting(phase: Phase): Phase {
    return {
      ...phase,
      start_date: this.formatDate(new Date(phase.start_date!)),
      end_date: this.formatDate(new Date(phase.end_date!))
    };
  }


  private handleSubscription(operation: Observable<any>): void {
    this.isLoading = true;
    this.subscriptions.add(
      operation.subscribe({
        next: (response: any) => {
          this.isFormVisible = false;
          this.isLoading = false;
          this._snackbarService.show(response.message);
          this.loadPhaseListData();
        },
        error: (error) => {
          console.error('There was an error', error);
          this.isLoading = false;
        }
      })
    );
  }


  createPhase(): void {
    this.phase.product = this.productId;
    const formattedPhase = this.applyDateFormatting(this.phase);
    this.handleSubscription(
      this._apiService.createPhase(formattedPhase)
    );
  }


  updatePhase(): void {
    const formattedPhase = this.applyDateFormatting(this.phase);
    this.handleSubscription(
      this._apiService.updatePhaseDetail(formattedPhase)
    );
  }

  confirmDelete(id: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    DialogConfirmComponent.openDeleteDialog(this._dialog, this._translate).subscribe(result => {
      if (result) {
        this.deletePhase(id);
      }
    });
  }


  deletePhase(id: any): void {
    this.handleSubscription(
      this._apiService.deletePhase(id)
    );
  }


  editPhase(phase: any): void {
    this.phase = {...phase};
    this.isFormVisible = true;
    this.isEditMode = true;
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}
